import React, { useCallback, useState, useEffect, useMemo } from "react";
import "./style.less";
import { useSelector, useDispatch } from "react-redux";
import ModifiedCalendar from "../../../common/components/ModifiedCalendar";
import moment from "moment";
import {
  fetchLeaves,
  applyLeaves,
  removeLeaves,
  fetchPendingTasksOfSelectedRMBuddy
} from "../../../redux/rm-buddies/actions";
import { Grid, Button } from "semantic-ui-react";
import TransferTaskButton from "../TransferTaskButton";

const getTaskCategory = task => {
  if (task.status === "PENDING" && moment(task.dueAt) < moment()) {
    return "DELAYED";
  } else if (task.status === "PENDING") {
    return "PENDING";
  }
};

const statusMapping = {
  RMBUDDY_COMPLETE: "Completed Task",
  PENDING: "Tasks in progress",
  REASSIGNED: "Transferred Task",
  DELAYED: "Delayed Task",
  UNALLOCATED: "Unallocated Tasks"
};

const withingLeaves = (date, leaves) => {
  if (!leaves) return false;
  for (let i = 0; i < leaves.length; i++) {
    if (
      moment(leaves[i].fromDate) <= date &&
      date <= moment(leaves[i].toDate)
    ) {
      return true;
    }
  }
  return false;
};

const areOnlyLeavesSelected = (selectedRange, leaves) => {
  let day = selectedRange[0].clone();
  while (day <= selectedRange[1]) {
    if (!withingLeaves(day, leaves)) {
      return false;
    }
    day.add(1, "d");
  }
  return true;
};

function RMBuddyLeaveCalendar({ onClose }) {
  const dispatch = useDispatch();
  const selectedRmBuddyLeaves = useSelector(
    ({ rmBuddies }) => rmBuddies.selectedRmBuddyLeaves
  );
  const [selectedDateRange, setSelectedDateRange] = useState([
    moment().startOf("day"),
    moment().endOf("day")
  ]);
  const [activeMonthRange, setActiveMonthRange] = useState([
    moment().startOf("month"),
    moment().endOf("month")
  ]);
  const pendingTasksOfSelectedRMBuddy = useSelector(
    ({ rmBuddies }) => rmBuddies.pendingTasksOfSelectedRMBuddy
  );

  const onlyWithinDateRange = useMemo(
    () =>
      pendingTasksOfSelectedRMBuddy &&
      pendingTasksOfSelectedRMBuddy.filter(
        t =>
          selectedDateRange[0] <= moment(t.dueAt) &&
          moment(t.dueAt) <= selectedDateRange[1]
      ),
    [pendingTasksOfSelectedRMBuddy, selectedDateRange]
  );
  const changeActiveMonth = ({ activeStartDate }) => {
    if (
      activeMonthRange[0] < activeStartDate.startOf("month") ||
      activeMonthRange[1] < activeStartDate.startOf("month")
    ) {
      setActiveMonthRange([
        activeStartDate.startOf("month"),
        activeStartDate.endOf("month")
      ]);
      dispatch(
        fetchLeaves({
          fromDate: moment(activeStartDate)
            .startOf("month")
            .format(),
          toDate: moment(activeStartDate)
            .endOf("month")
            .format()
        })
      );
    }
  };

  const onlyLeaveSelected = useMemo(
    () => areOnlyLeavesSelected(selectedDateRange, selectedRmBuddyLeaves),
    [selectedDateRange, selectedRmBuddyLeaves]
  );

  const addLeaveDateClass = ({ date, view }) =>
    view === "month" && withingLeaves(date, selectedRmBuddyLeaves)
      ? "leaveMark"
      : null;

  const onDateRangeChange = dateRange => {
    setSelectedDateRange([
      dateRange[0].startOf("day"),
      dateRange[1].endOf("day")
    ]);
  };
  useEffect(() => {
    if (!selectedRmBuddyLeaves) {
      dispatch(
        fetchLeaves({
          fromDate: activeMonthRange[0]
            .clone()
            .startOf("day")
            .format(),
          toDate: activeMonthRange[1]
            .clone()
            .endOf("day")
            .format()
        })
      );
    }
  }, [selectedRmBuddyLeaves, activeMonthRange, dispatch]);

  useEffect(() => {
    dispatch(
      fetchPendingTasksOfSelectedRMBuddy({
        fromDate: selectedDateRange[0].clone().endOf("day"),
        toDate: selectedDateRange[1].clone().endOf("day")
      })
    );
  }, [dispatch, selectedDateRange]);

  const markLeaves = useCallback(() => {
    dispatch(
      applyLeaves({
        fromDate: selectedDateRange[0]
          .clone()
          .startOf("day")
          .format(),
        toDate: selectedDateRange[1]
          .clone()
          .endOf("day")
          .format(),
        onSuccessAction: fetchLeaves({
          fromDate: activeMonthRange[0]
            .clone()
            .startOf("day")
            .format(),
          toDate: activeMonthRange[1]
            .clone()
            .endOf("day")
            .format()
        })
      })
    );
    setSelectedDateRange([moment().startOf("day"), moment().endOf("day")]);
  }, [selectedDateRange, dispatch, activeMonthRange]);

  const unmarkLeaves = useCallback(() => {
    dispatch(
      removeLeaves({
        fromDate: selectedDateRange[0]
          .clone()
          .startOf("day")
          .format(),
        toDate: selectedDateRange[1]
          .clone()
          .endOf("day")
          .format(),
        onSuccessAction: fetchLeaves({
          fromDate: activeMonthRange[0]
            .clone()
            .startOf("day")
            .format(),
          toDate: activeMonthRange[1]
            .clone()
            .endOf("day")
            .format()
        })
      })
    );
    setSelectedDateRange([moment().startOf("day"), moment().endOf("day")]);
  }, [selectedDateRange, dispatch, activeMonthRange]);

  const notCompletedTasksbucketedByDueDate = useMemo(() => {
    if (!onlyWithinDateRange) {
      return {};
    }
    const result = {};
    onlyWithinDateRange.forEach(e => {
      let key = moment(e.dueAt).format("MMMM DD, YYYY");
      if (result[key]) {
        result[key].push(e);
      } else {
        result[key] = [e];
      }
    });
    return result;
  }, [onlyWithinDateRange]);

  const canMarkAbsent = useMemo(() => {
    if (!onlyWithinDateRange) {
      return true;
    }
    return onlyWithinDateRange.length === 0;
  }, [onlyWithinDateRange]);

  return (
    <div className="RMBuddyLeaveCalendar">
      <Grid>
        <Grid.Column width={8}>
          <h4 style={{ margin: 0 }}>
            Selected Dates: {selectedDateRange[0].format("DD MMM YYYY")}-
            {selectedDateRange[1].format("DD MMM YYYY")}
          </h4>
          <ModifiedCalendar
            onChange={onDateRangeChange}
            selectRange={true}
            dateRange={selectedDateRange}
            tileClassName={addLeaveDateClass}
            onActiveStartDateChange={changeActiveMonth}
            view={"month"}
          />
          <Button
            basic
            onClick={onlyLeaveSelected ? unmarkLeaves : markLeaves}
            color={!canMarkAbsent ? "grey" : "red"}
            disabled={!onlyLeaveSelected ? !canMarkAbsent : false}
          >
            Mark {onlyLeaveSelected ? "Available" : "Absent"}
          </Button>
          {!canMarkAbsent && "Transfer tasks before marking them as absent"}
        </Grid.Column>
        <Grid.Column width={8}>
          <div class="PendingTaskDetalContainer">
            {Object.keys(notCompletedTasksbucketedByDueDate).map(nc => (
              <div>
                <h4>{nc}</h4>
                {notCompletedTasksbucketedByDueDate[nc].map((e, i) => (
                  <div key={i} className="PendingTaskDetail">
                    <Grid>
                      <Grid.Column width={4}>
                        <div className="RmBuddyAvatar">
                          <div className="RmBuddyAvatarInitials">
                            {e.assignedTo
                              ? `${e.assignedTo.name.charAt(
                                  0
                                )}${e.assignedTo.name.charAt(1)}`.toUpperCase()
                              : "!!"}
                          </div>
                        </div>
                      </Grid.Column>
                      <Grid.Column width={10}>
                        <p className="TasksId">
                          Tasks ID: #{e.taskUniqueId.substring(0, 5)}...
                          {e.taskUniqueId.substring(25)}
                        </p>
                        <p className="TasksType">Task Type: {e.taskType}</p>
                        <p className="TasksAssignedTo">
                          RM Buddy: {e.assignedTo ? e.assignedTo.name : "NULL"}
                        </p>
                        <p className={getTaskCategory(e)}>
                          {statusMapping[getTaskCategory(e)]}
                        </p>
                      </Grid.Column>
                    </Grid>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!canMarkAbsent && (
            <TransferTaskButton
              style={{
                position: "absolute",
                right: "0px",
                bottom: "15px"
              }}
              text={"Transfer Tasks"}
              taskIds={
                onlyWithinDateRange && onlyWithinDateRange.map(e => e._id)
              }
              onTransferAction={fetchPendingTasksOfSelectedRMBuddy({
                fromDate: selectedDateRange[0].clone().endOf("day"),
                toDate: selectedDateRange[1].clone().endOf("day")
              })}
              taskAssignedTo={
                onlyWithinDateRange && onlyWithinDateRange.length > 0
                  ? [onlyWithinDateRange[0].assignedTo]
                  : []
              }
            />
          )}
        </Grid.Column>
      </Grid>
    </div>
  );
}

export default RMBuddyLeaveCalendar;
